import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { Provider } from 'react-redux';
import TagManager from 'react-gtm-module';
// root app page
import App from 'app';
// providers
import { ProjectStepperContextProvider } from './hooks/useProjectStepperState';
import { ToastContextProvider } from 'hooks/useToast';
import { ProjectProvider } from 'hooks/useProjectDetails';
// store
import { store } from 'redux-store/store';
// styles
import 'swiper/css';
import './index.css';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

declare global {
  interface Window {
    jQuery?: any;
  }
}

// Load jQuery before GTM if not already loaded (required by GTM tags)
const loadJQueryIfNeeded = (): Promise<void> => {
  return new Promise((resolve) => {
    if (window.jQuery) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://code.jquery.com/jquery-3.7.1.min.js';
    script.integrity = 'sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo=';
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => {
      console.warn('Failed to load jQuery, GTM may not work correctly');
      resolve(); // Continue anyway
    };
    document.head.appendChild(script);
  });
};

const tagManagerArgs = {
  gtmId: 'GTM-T8FPR29',
};
console.log('App Env: ', process.env.REACT_APP_ENV);
if (process.env.REACT_APP_ENV === 'Production') {
  loadJQueryIfNeeded().then(() => {
    TagManager.initialize(tagManagerArgs);
  });
}

const domNode = document.getElementById('root')!;
const root = createRoot(domNode);

datadogRum.init({
  applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID,
  clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
  site: 'datadoghq.com',
  service: 'frontend',
  env: process.env.REACT_APP_DATADOG_ENV?.toLowerCase() || 'production',

  // Specify a version number to identify the deployed version of your application in Datadog
  // version: '1.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  defaultPrivacyLevel: 'allow',
  plugins: [reactPlugin({ router: true })],
});

root.render(
  <BrowserRouter>
    <Provider store={store}>
      <ProjectProvider>
        <ProjectStepperContextProvider>
          <ToastContextProvider>
            <SWRConfig value={{ provider: () => new Map() }}>
              <App />
            </SWRConfig>
          </ToastContextProvider>
        </ProjectStepperContextProvider>
      </ProjectProvider>
    </Provider>
  </BrowserRouter>
);
